import * as React from 'react'

import { NavMain } from '@/components/nav-main'
import { NavPinned } from '@/components/nav-pinned'
import { NavUser } from '@/components/nav-user'
import { SidebarSearch } from '@/components/sidebar-search'
import { TeamSwitcher } from '@/components/team-switcher'
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarRail } from '@/components/ui/sidebar'
import { Skeleton } from '@/components/ui/skeleton'
import { adminMenu, orgMenu, vendorMenu } from '@/constant/sidebar'
import type { Role } from '@/enum/global.enum'
import { useActiveOrg } from '@/hooks/use-active-org'
import type { IUserOrganisations } from '@/interface/auth.interface'
import type { IClerkUser } from '@/interface/clerk.interface'
import { PinnedSidebarProvider } from '@/providers/pinned-sidebar.provider'
import { SidebarSearchProvider } from '@/providers/sidebar-search.provider'
import { isAdminRole, isOrgRole, isVendorRole } from '@/utils/role-check.utils'

export function AppSidebar({
  user,
  organisations,
  ...props
}: React.ComponentProps<typeof Sidebar> & {
  user: IClerkUser | null | undefined
  organisations: IUserOrganisations['organisations'] | null
}) {
  return (
    <SidebarSearchProvider>
      <PinnedSidebarProvider>
        <AppSidebarContent user={user} organisations={organisations} {...props} />
      </PinnedSidebarProvider>
    </SidebarSearchProvider>
  )
}

function AppSidebarContent({
  user,
  organisations,
  ...props
}: React.ComponentProps<typeof Sidebar> & {
  user: IClerkUser | null | undefined
  organisations: IUserOrganisations['organisations'] | null
}) {
  const { activeOrg } = useActiveOrg()

  return (
    <Sidebar collapsible='icon' {...props}>
      <SidebarHeader>
        <TeamSwitcher organisations={organisations} role={activeOrg?.role as Role} />
        <SidebarSearch />
      </SidebarHeader>
      <SidebarContent id='sidebar-content'>
        {activeOrg?.role && isAdminRole(activeOrg.role) ? (
          <>
            <NavPinned />
            <NavMain items={adminMenu.navMain} title='Menu' />
          </>
        ) : activeOrg?.role && isVendorRole(activeOrg.role) ? (
          <NavMain items={vendorMenu.navMain} title='Menu' />
        ) : activeOrg?.role && isOrgRole(activeOrg.role) ? (
          <>
            <NavPinned />
            <NavMain items={orgMenu.navMain} title='Menu' />
            <NavMain items={orgMenu.others} title='Administration' />
          </>
        ) : (
          <>
            <Skeleton className='h-20 w-[90%] rounded-lg mx-auto' />
            <Skeleton className='h-10 w-[90%] rounded-lg mx-auto' />
            <Skeleton className='h-15 w-[90%] rounded-lg mx-auto' />
            <Skeleton className='h-10 w-[90%] rounded-lg mx-auto' />
            <Skeleton className='h-15 w-[90%] rounded-lg mx-auto' />
            <Skeleton className='h-20 w-[90%] rounded-lg mx-auto' />
          </>
        )}
      </SidebarContent>
      <SidebarFooter>
        <NavUser
          name={user?.fullName ?? ''}
          email={user?.primaryEmailAddress?.emailAddress ?? ''}
          imageUrl={user?.imageUrl ?? ''}
          role={activeOrg?.role as Role}
        />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
